// NOT a byte copy of apps/tray/src/worker-client.js. Every difference, and why the coffee-cup page needs it:
// 1. `replace()` — swaps the worker at once. An `engine` reply means the wasm engine inside the worker is dead, and
//    an uploaded file can keep the engine busy past the page's limit; recycleIfDue() fixes neither (it only runs when
//    idle and past `recycleAfter`). Calls the old worker had are answered `replaced`; a call that was still waiting
//    for the old worker to start goes to the new one.
// 2. A worker that cannot work ends the client, by one path (`fail`): a `fatal` message (as in tray's), a Worker
//    `error` or `messageerror` event (a script that did not load raises only that), or no `ready` within
//    `startLimitMs()` (a script that loads and never answers raises nothing at all). The worker is terminated,
//    `onFatal` is called once, and every call — waiting, posted or later — is answered { code: 'fatal' }; replace()
//    and recycleIfDue() then start no new worker. In tray's copy such calls wait for good.
// 3. `callOnce(msg, { limitMs })` — a call that belongs to ONE worker and may carry a clock (for an upload).
// 4. Option `startLimitMs` (a function, read each time a worker is started).
// 5. No `whenReady()`: nothing on this page waits for readiness apart from a call.
// 6. (unify-ui ship 2) `callOnce(msg, { limitMs, from: 'started' })` — a clock that starts when the WORKER says it has
//    started the request (its `started` message), for a BUILD: the worker runs one request at a time and waits for a
//    font file before it starts one, and neither wait is the build's own work (ruling R22). The upload's clock keeps
//    its start at the post (`from` absent), as it always had. `onProgress` reaches a callOnce too.
// The main thread's side of the geometry worker: start, wait for ready, call, recycle.
// manifold-3d 3.5.3 leaked ~4KB of wasm heap per evaluated extrude even when every handle was
// deleted (measured 2026-09-21: ~80KB per tag); that polygon-vector leak is patched in
// packages/core/src/manifold-loader.js. A wasm heap never shrinks, so the worker is still replaced
// after `recycleAfter` meshes/exports, while idle — kept as a safety net, not re-measured after the patch. Layout requests are 2D
// only and do not count.
const MAX_TIMER_MS = 2147483647;    // setTimeout fires at once above this (and for Infinity)
const failed = (code, id) => ({ id, ok: false, error: { code } });

export function createWorkerClient({ url, recycleAfter = 300, onFatal, startLimitMs = () => 60000 }) {
  let worker = null, ready = null, generations = 0, reqId = 0, dead = false;
  const pending = new Map();
  function start() {
    const w = new Worker(url, { type: 'module' });
    generations = 0;
    ready = new Promise(res => { w.onceReady = res; });
    w.onmessage = (ev) => {
      const { type, id } = ev.data;
      if (type === 'ready') { clearTimeout(w.startTimer); w.onceReady(); return; }
      if (type === 'fatal') { fail(w, ev.data.message); return; }
      if (type === 'progress') { for (const pid of ev.data.ids) pending.get(pid)?.onProgress?.(ev.data); return; }   // one download, many waiters
      if (type === 'started') { pending.get(id)?.onStarted?.(); return; }                                            // the worker is on this request now
      const cb = pending.get(id);
      if (!cb) return;
      pending.delete(id);
      cb.resolve(ev.data);
    };
    w.onerror = (ev) => fail(w, `worker error: ${(ev && ev.message) || 'the script did not load or crashed'}`);
    w.onmessageerror = () => fail(w, 'worker error: a message could not be read');
    w.startTimer = setTimeout(() => fail(w, 'worker error: not ready in time'), startLimitMs());
    worker = w;
  }
  // This worker cannot work, and the page has no other: end it, answer everybody, tell the page once. Settling the
  // worker's `ready` wakes the calls that were waiting for it; they see `dead` and answer `fatal` without posting.
  function fail(w, message) {
    if (dead || w !== worker) return;
    dead = true;
    clearTimeout(w.startTimer);
    w.terminate();
    for (const [id, cb] of [...pending]) { pending.delete(id); cb.resolve(failed('fatal', id)); }
    w.onceReady();
    onFatal(message);
  }
  // Always waits for the CURRENT worker to be ready: the worker ignores messages that arrive
  // before its 'ready', so posting early would leave this promise pending forever.
  async function call(msg, { onProgress } = {}) {
    let w;
    do { w = worker; await ready; } while (w !== worker);          // the worker may be recycled while we wait
    if (dead) return failed('fatal');
    return new Promise(resolve => {
      const id = ++reqId;                                          // the sequence number the reply echoes
      pending.set(id, { resolve, onProgress });
      worker.postMessage({ ...msg, id });
    });
  }
  // One message for ONE worker — the one that is current when this is called — with an optional clock, for a request
  // the engine may never finish (an uploaded file). If that worker is swapped before it is ready, nothing is posted
  // and the answer is `replaced`: the caller decides whether to start over. The clock starts when the message is
  // posted, so the worker's start never counts against it and the message can never go out after its clock has
  // fired — or, with `from: 'started'`, when the worker says it has started the request (difference 6 above).
  // A limit that is not a number in 1..2147483647 ms is no clock. A clock that fires answers `timeout` and drops the
  // request; ending the worker is the caller's decision.
  async function callOnce(msg, { limitMs, from, onProgress } = {}) {
    const w = worker;
    await ready;
    if (dead) return failed('fatal');
    if (w !== worker) return failed('replaced');
    return new Promise(resolve => {
      const id = ++reqId;
      let timer = null;
      const arm = () => { if (limitMs > 0 && limitMs <= MAX_TIMER_MS && timer === null) timer = setTimeout(() => { if (pending.delete(id)) resolve(failed('timeout', id)); }, limitMs); };
      pending.set(id, { resolve: (reply) => { clearTimeout(timer); resolve(reply); }, onStarted: arm, onProgress });   // arm() arms once: for an upload it has, at the post below
      w.postMessage({ ...msg, id });
      if (from !== 'started') arm();                               // the upload's clock: from the post
    });
  }
  function recycleIfDue() {
    if (dead || generations < recycleAfter || pending.size > 0) return;
    worker.terminate();
    start();
  }
  // The wasm engine inside the worker is dead (an `engine` reply), or busy past the caller's patience: drop it and
  // start a fresh one. Requests the old worker had are answered `replaced` — the caller decides whether to ask again.
  // A call that was still waiting for the old worker to become ready was never posted, so it is not in `pending`:
  // the old worker's `ready` is settled here, which wakes that call; it then sees a different worker and waits for
  // the new one (the loop in call()) or answers `replaced` (callOnce). Without this it would wait for a `ready`
  // that can no longer come.
  function replace() {
    if (dead) return;
    const old = worker;
    clearTimeout(old.startTimer);
    old.terminate();
    for (const [id, cb] of [...pending]) { pending.delete(id); cb.resolve(failed('replaced', id)); }
    start();
    old.onceReady();
  }
  start();
  return { call, callOnce, recycleIfDue, replace, countGeneration: () => { generations++; } };
}
